import React from "react";
import {
  PeriodReportData,
  ReportCategory,
} from "@/api/fetchQuickBooksPeriodReport/types";

interface PeriodReportTableProps {
  report: PeriodReportData | null;
}

interface CategoryRowProps {
  category: ReportCategory;
  level: number;
  derived?: boolean;
}

// Plain QuickBooks number: thousands separators, 2 decimals, leading minus,
// no currency symbol (e.g. "8,912.80", "-4,020.13"). Used on account rows.
const formatPlain = (amount: number): string => {
  return new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
};

// Bold-total QuickBooks number: currency with leading minus on the value
// rather than parentheses (e.g. "$4,892.67", "-$912.95"). Used on
// "Total for {name}" rows and derived rows.
const formatTotal = (amount: number): string => {
  const formatted = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(Math.abs(amount));
  return amount < 0 ? `-${formatted}` : formatted;
};

// Percent of income as a whole-number-ish percentage with 2 decimals and a
// leading minus for negatives (e.g. "40.31%", "-5.88%"). Source value is a
// fraction (0.4031 = 40.31%).
const formatPercent = (value: number): string => `${(value * 100).toFixed(2)}%`;

const CategoryRow: React.FC<CategoryRowProps> = ({
  category,
  level,
  derived = false,
}) => {
  const paddingLeft = level * 24;
  // A node is a header (group/section) when it has children. Derived rows
  // (Gross Profit, Net Operating Income, Net Income) have no children but
  // render as bold dollar single lines.
  const isHeader = category.subcategories.length > 0;
  const isDerived = derived;

  return (
    <>
      <tr
        className={`
        border-b border-gray-200 dark:border-slate-700
        ${isHeader || isDerived ? "bg-gray-100 dark:bg-slate-700/50 font-semibold" : "hover:bg-gray-50 dark:hover:bg-slate-700"}
        ${level === 0 ? "border-t-2 border-gray-300 dark:border-slate-600" : ""}
      `}
      >
        <td
          className="py-3 px-4 text-gray-900 dark:text-slate-100"
          style={{ paddingLeft: `${paddingLeft}px` }}
        >
          {category.name}
          {category.accountCode && (
            <span className="text-gray-500 dark:text-slate-400 ml-2 text-sm">
              ({category.accountCode})
            </span>
          )}
        </td>
        <td className="py-3 px-4 text-right text-gray-900 dark:text-slate-100">
          {isDerived && category.amount !== null
            ? formatTotal(category.amount)
            : category.amount !== null
              ? formatPlain(category.amount)
              : ""}
        </td>
        <td className="py-3 px-4 text-right text-gray-900 dark:text-slate-100">
          {category.total === null
            ? formatPercent(category.percentOfRevenue)
            : ""}
        </td>
      </tr>
      {category.subcategories.map(
        (subcategory: ReportCategory, index: number) => (
          <CategoryRow
            category={subcategory}
            key={`${subcategory.name}-${index}`}
            level={level + 1}
          />
        ),
      )}
      {category.total !== null && (
        <tr className="border-b border-gray-200 dark:border-slate-700 bg-gray-100 dark:bg-slate-700/50 font-semibold">
          <td
            className="py-3 px-4 text-gray-900 dark:text-slate-100"
            style={{ paddingLeft: `${paddingLeft}px` }}
          >
            Total for {category.name}
          </td>
          <td className="py-3 px-4 text-right text-gray-900 dark:text-slate-100">
            {formatTotal(category.total)}
          </td>
          <td className="py-3 px-4 text-right text-gray-900 dark:text-slate-100">
            {formatPercent(category.percentOfRevenue)}
          </td>
        </tr>
      )}
    </>
  );
};

export const PeriodReportTable: React.FC<PeriodReportTableProps> = ({
  report,
}) => {
  if (!report) {
    return null;
  }

  return (
    <div className="rounded-md border border-gray-200 dark:border-slate-700 overflow-x-auto bg-white dark:bg-slate-800">
      <table className="min-w-full text-sm text-gray-900 dark:text-slate-100">
        <thead>
          <tr className="border-b border-gray-200 dark:border-slate-700 bg-gray-50 dark:bg-slate-800">
            <th
              className="py-3 px-4 text-left text-xs font-medium text-gray-500 dark:text-slate-400 uppercase tracking-wider"
              scope="col"
            >
              Account
            </th>
            <th
              className="py-3 px-4 text-right text-xs font-medium text-gray-500 dark:text-slate-400 uppercase tracking-wider"
              scope="col"
            >
              Total
            </th>
            <th
              className="py-3 px-4 text-right text-xs font-medium text-gray-500 dark:text-slate-400 uppercase tracking-wider"
              scope="col"
            >
              % of Income
            </th>
          </tr>
        </thead>
        <tbody>
          <CategoryRow category={report.revenue} level={0} />
          <CategoryRow category={report.costOfGoodsSold} level={0} />
          <CategoryRow category={report.grossProfit} derived level={0} />
          <CategoryRow category={report.operatingExpenses} level={0} />
          <CategoryRow category={report.operatingIncome} derived level={0} />
          <CategoryRow category={report.otherIncomeExpenses} level={0} />
          <CategoryRow category={report.netIncome} derived level={0} />
        </tbody>
      </table>
    </div>
  );
};
